import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { formatPrice, formatDate } from "../utils/format";
import { RotateCcw, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";

const REASONS = ["Product defective/damaged", "Wrong item received", "Item not as described", "Changed my mind", "Duplicate order", "Other"];

export default function ReturnRequestPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [myReturns, setMyReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ orderId: "", orderItem: "", reason: "", details: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getDocs(query(collection(db, "orders"), where("userId", "==", user.uid))),
      getDocs(query(collection(db, "returns"), where("userId", "==", user.uid))),
    ]).then(([oSnap, rSnap]) => {
      const ordersList = oSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => o.status === "delivered")
        .sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      const returnsList = rSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      setOrders(ordersList);
      setMyReturns(returnsList);
      setLoading(false);
    });
  }, [user]);

  const selectedOrder = orders.find(o => o.id === form.orderId);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.orderId || !form.reason) return toast.error("Please fill all required fields");
    setSubmitting(true);
    try {
      await addDoc(collection(db, "returns"), {
        userId: user.uid,
        userEmail: user.email,
        orderId: form.orderId,
        orderItem: form.orderItem,
        reason: form.reason,
        details: form.details,
        status: "pending",
        refundAmount: null,
        createdAt: serverTimestamp(),
      });
      toast.success("Return request submitted!");
      setSubmitted(true);
    } catch (err) { toast.error("Failed to submit. Try again."); }
    finally { setSubmitting(false); }
  }

  if (loading) return <div className="loader" style={{ minHeight: "60vh" }}><div className="spinner" /></div>;

  if (submitted) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 440 }}>
        <div style={{ width: 72, height: 72, background: "#DCFCE7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <CheckCircle size={36} color="var(--success)" />
        </div>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Return Request Submitted</h2>
        <p style={{ color: "var(--text-muted)", lineHeight: 1.7 }}>Our team will review your request within 24–48 hours and contact you via email with next steps.</p>
        <button onClick={() => setSubmitted(false)} className="btn btn-primary" style={{ marginTop: 24 }}>Submit Another</button>
      </div>
    </div>
  );

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh" }}>
      <div className="container section-sm" style={{ maxWidth: 680 }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 26, fontWeight: 700 }}>Return / Refund Request</h1>
          <p style={{ color: "var(--text-muted)", marginTop: 6 }}>We accept returns within 30 days of delivery for eligible items.</p>
        </div>

        {/* Existing returns */}
        {myReturns.length > 0 && (
          <div className="card" style={{ padding: 24, marginBottom: 28 }}>
            <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16, marginBottom: 16 }}>My Return Requests</h3>
            {myReturns.map(r => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Order #{r.orderId?.slice(-6).toUpperCase()}</div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{r.reason} · {formatDate(r.createdAt)}</div>
                  {r.refundAmount && <div style={{ fontSize: 13, color: "var(--success)", fontWeight: 500 }}>Refund: {formatPrice(r.refundAmount)}</div>}
                </div>
                <span className={`badge badge-${r.status === "approved" ? "success" : r.status === "rejected" ? "danger" : "warning"}`} style={{ textTransform: "capitalize" }}>{r.status}</span>
              </div>
            ))}
          </div>
        )}

        {orders.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
            <RotateCcw size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p>No eligible orders for return. Only delivered orders can be returned.</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 28 }}>
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div className="form-group">
                <label>Select Order *</label>
                <select className="form-select" required value={form.orderId} onChange={e => setForm(f => ({ ...f, orderId: e.target.value, orderItem: "" }))}>
                  <option value="">Choose an order</option>
                  {orders.map(o => (
                    <option key={o.id} value={o.id}>
                      Order #{o.id.slice(-6).toUpperCase()} — {formatPrice(o.total)} · {formatDate(o.createdAt)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedOrder && (
                <div className="form-group">
                  <label>Which item? (optional)</label>
                  <select className="form-select" value={form.orderItem} onChange={e => setForm(f => ({ ...f, orderItem: e.target.value }))}>
                    <option value="">Entire order</option>
                    {selectedOrder.items?.map((item, i) => (
                      <option key={i} value={item.name}>{item.name} × {item.qty} — {formatPrice(item.price * item.qty)}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Reason for Return *</label>
                <select className="form-select" required value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}>
                  <option value="">Select a reason</option>
                  {REASONS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Additional Details</label>
                <textarea className="form-input" rows={4} placeholder="Describe the issue in detail. The more info you provide, the faster we can resolve it."
                  value={form.details} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} style={{ resize: "vertical" }} />
              </div>

              <div style={{ padding: 14, background: "var(--surface)", borderRadius: 10, fontSize: 13, color: "var(--text-muted)" }}>
                📋 <strong>Return Policy:</strong> Items must be unused, in original packaging. Refunds are processed within 5–7 business days after approval.
              </div>

              <button type="submit" disabled={submitting} className="btn btn-primary btn-lg">
                {submitting ? "Submitting…" : "Submit Return Request"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
